import { lazy, Suspense } from 'react';
import { Route, Routes, Navigate } from "react-router-dom";
import Loading from "../Components/Loading";
import LoginPage from "../Components/Login";
import PrivateRoute from "../Components/PrivateRoute";

// Lazy load route components
const AdminPage = lazy(() => import("../Containers/Admin"));
const Admitted = lazy(() => import('../Containers/Reports/Admitted'));
const System = lazy(() => import('../Containers/Reports/System'));
const Audit = lazy(() => import('../Containers/Reports/Audit'));
const SubjectComponent = lazy(() => import('../Containers/Reports/Subject'));
const General = lazy(() => import('../Containers/Statistics/General'));
const Program = lazy(() => import('../Containers/Statistics/Subject'));
const Score = lazy(() => import('../Containers/Statistics/Score'));
const SchoolsMapComponent = lazy(() => import('../Containers/Statistics/SchoolsMap'));
const SchoolsStatisticsComponent = lazy(() => import('../Containers/Statistics/Schools'));
const IcfesComponent = lazy(() => import('../Containers/Reports/Icfes'));
const SchoolsComponent = lazy(() => import('../Containers/Reports/Schools'));
const PersonalData = lazy(() => import('../Containers/Reports/Data'));
const Inconsistencies = lazy(() => import('../Containers/Reports/Inconsistencies'));
const Dashboard = lazy(() => import('../Containers/Dashboard'));
const FileUpload = lazy(() => import('../Containers/Reports/FileUpload'));
const CandidateUpload = lazy(() => import('../Containers/Reports/CandidateUpload'));
const UserManagement = lazy(() => import('../Containers/Admin/UserManagement'));
const OfferManagement = lazy(() => import('../Containers/Admin/OfferManagement'));
const FacultyManagement = lazy(() => import('../Containers/Admin/FacultyManagement'));
const ProgramManagement = lazy(() => import('../Containers/Admin/ProgramManagement'));
const CalculationModelManagement = lazy(() => import('../Containers/Admin/CalculationModelManagement'));
const QuotaManagement = lazy(() => import('../Containers/Admin/QuotaManagement'));
const Scores = lazy(() => import('../Containers/Reports/Scores'));
const AdmissionsManagement = lazy(() => import('../Containers/Admin/AdmissionsManagement'));
const SpecialQuotasManagement = lazy(() => import('../Containers/Admin/SpecialQuotasManagement'));
const NewSpecialQuotasManagement = lazy(() => import('../Containers/Admin/NewSpecialQuotasManagement'));
const SpecialQuotasCentersManagement = lazy(() => import('../Containers/Admin/SpecialQuotasCentersManagement'));
const SpecialQuotasSharedManagement = lazy(() => import('../Containers/Admin/SpecialQuotasSharedManagement'));
const VisitsManagement = lazy(() => import('../Containers/Admin/VisitsManagement'));
const OfferSelectionPrograms = lazy(() => import('../Containers/Admin/OfferSelectionPrograms'));

const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<Loading loading={true} />}>{children}</Suspense>
);

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin" />} />
      <Route path="*" element={<Navigate to="/login" />} />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <SuspenseWrapper>
              <AdminPage />
            </SuspenseWrapper>
          </PrivateRoute>
        }
      >
        <Route
          path="dashboard"
          element={
            <SuspenseWrapper>
              <Dashboard />
            </SuspenseWrapper>
        }
        />
        <Route
          path="admitted"
          element={
            <SuspenseWrapper>
              <Admitted />
            </SuspenseWrapper>
        }
        />
        <Route
          path="system"
          element={
            <SuspenseWrapper>
              <System />
            </SuspenseWrapper>
          }
        />
        <Route
          path="audit"
          element={
            <SuspenseWrapper>
              <Audit />
            </SuspenseWrapper>
          }
        />
        <Route
          path="subject"
          element={
            <SuspenseWrapper>
              <SubjectComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="general"
          element={
            <SuspenseWrapper>
              <General />
            </SuspenseWrapper>
          }
        />
        <Route
          path="program"
          element={
            <SuspenseWrapper>
              <Program />
            </SuspenseWrapper>
          }
        />
        <Route
          path="scores"
          element={
            <SuspenseWrapper>
              <Score />
            </SuspenseWrapper>
          }
        />
        <Route
          path="schools-map"
          element={
            <SuspenseWrapper>
              <SchoolsMapComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="schools-statistics"
          element={
            <SuspenseWrapper>
              <SchoolsStatisticsComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="icfes"
          element={
            <SuspenseWrapper>
              <IcfesComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="schools"
          element={
            <SuspenseWrapper>
              <SchoolsComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="match"
          element={
            <SuspenseWrapper>
              <PersonalData />
            </SuspenseWrapper>
          }
        />
        <Route
          path="review"
          element={
            <SuspenseWrapper>
              <Inconsistencies />
            </SuspenseWrapper>
          }
        />
        <Route
          path="candidate-scores"
          element={
            <SuspenseWrapper>
              <Scores />
            </SuspenseWrapper>
          }
        />
        <Route
          path="upload"
          element={
            <SuspenseWrapper>
              <FileUpload />
            </SuspenseWrapper>
          }
        />
        <Route
          path="upload-candidates"
          element={
            <SuspenseWrapper>
              <CandidateUpload />
            </SuspenseWrapper>
          }
        />
        <Route
          path="users"
          element={
            <SuspenseWrapper>
              <UserManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="offers"
          element={
            <SuspenseWrapper>
              <OfferManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="faculties"
          element={
            <SuspenseWrapper>
              <FacultyManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="programs"
          element={
            <SuspenseWrapper>
              <ProgramManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="calculation-models"
          element={
            <SuspenseWrapper>
              <CalculationModelManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="quotas"
          element={
            <SuspenseWrapper>
              <QuotaManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="admissions"
          element={
            <SuspenseWrapper>
              <AdmissionsManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="special-quotas"
          element={
            <SuspenseWrapper>
              <SpecialQuotasManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="special-quotas-new"
          element={
            <SuspenseWrapper>
              <NewSpecialQuotasManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="special-quotas-centers"
          element={
            <SuspenseWrapper>
              <SpecialQuotasCentersManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="special-quotas-shared"
          element={
            <SuspenseWrapper>
              <SpecialQuotasSharedManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="visits"
          element={
            <SuspenseWrapper>
              <VisitsManagement />
            </SuspenseWrapper>
          }
        />
        <Route
          path="offer-selection-programs"
          element={
            <SuspenseWrapper>
              <OfferSelectionPrograms />
            </SuspenseWrapper>
          }
        />
      </Route>
      <Route path="/login" element={<LoginPage />} />
    </Routes>
  );
};

export default AppRoutes;
